/* eslint-disable react/no-multi-comp */
import { ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import React, { Children, FC } from 'react';
import { testId as testIdFn } from '@root/utils/e2e';
import { TABS_VARIANT } from '@root/constants';
import useCustomTheme from '@root/hooks/useCustomTheme';

type TTabsLayout = {
  children?: any;
  tabs: string[];
  title?: string;
  titleVariant?: string;
  ghost?: boolean;
  action?: TObject;
  testId?: string;
  variant?: TABS_VARIANT;
  enabled?: boolean;
  onTabChange?: (tabIndex: number) => void;
  tabIndex?: number;
  isRtl?: boolean,
  dir?: string,
  noBottomMargin?: boolean,
  padding?: string;
  onTabClick?: (tabIndex: number) => void;
  isLazy?: boolean;
  showRedDotOnTabIndex?: number[];
};

const StyledTab: FC<TObject> = ({ title, enabled, showRedDot, isDarkTheme, ...rest }) => (
  <Tab
    fontWeight='600'
    fontSize='16px'
    _selected={{ color: isDarkTheme ? '#FFF' : '#1782FF', bgColor: 'theme.alert.200' }}
    isDisabled={enabled}
    borderRadius={'8px'}
    height={'55px'}
    color={'theme.gray.500'}
    {...rest}
  >
    {title} {showRedDot &&
      <span style={{ padding: '2px', fontSize: 'x-large', marginTop: '-25px', color: 'red' }}>{'.'}</span>}
  </Tab>
);

export const TabsLayout: FC<TTabsLayout> = ({
  children,
  tabs,
  title,
  titleVariant = 'default',
  ghost,
  action: Action,
  testId,
  variant,
  enabled,
  onTabChange,
  tabIndex,
  isRtl = false,
  noBottomMargin = false,
  padding = '4',
  showRedDotOnTabIndex = [],
  onTabClick,
  isLazy = false,
}) => {
  const panels = Children.toArray(children);
  const { isDarkTheme } = useCustomTheme()

  const getTextStyle = () => {
    if (titleVariant === 'default') {
      return { fontWeight: '700', fontSize: '20px' };
    } else if (titleVariant === 'medium') {
      return { fontWeight: '500', fontSize: '16px' };
    }

    return {};
  };

  const renderChevron = () => {
    if (isRtl) {
      return <ChevronLeftIcon color='gray' />;
    }

    return <ChevronRightIcon color='gray' />;
  };


  return (
    <Box maxW={'100%'} p={padding}>
      <Tabs
      width={'100%'}
        variant='soft-rounded'
        colorScheme='gray'
        fontSize="16px"
        {...onTabChange ? { onChange: onTabChange } : {}}
        {...tabIndex !== undefined && tabIndex >= 0 ? { index: tabIndex } : {}}
        isLazy={isLazy}
      >
        <TabList
            width={'100%'}
          justifyContent="center"
          borderBottom="1px solid"
          borderColor="theme.gray.200"
          padding='16px'
          mb={noBottomMargin ? '0' : '4'}
        >
          <Flex alignItems='center' justifyContent='space-between'     width={'100%'}>
            <Box width={'100%'}>
              {title && (
                <>
                  <Text {...getTextStyle()}>{title}</Text>
                  {renderChevron()}
                </>
              )}

              <Flex
                flexWrap={'wrap'}
                alignItems='center'
                gap={'12px'}
                pb={'16px'}
              >
                {tabs.map((tab: string, i) => (
                  <StyledTab
                    height={'43px'}
                    key={tab}
                    title={tab}
                    showRedDot={showRedDotOnTabIndex.includes(i)}
                    {...testId ? testIdFn(`${testId}-tab-${i}`) : {}}
                    variant={variant}
                    onClick={onTabClick}
                    isDarkTheme={isDarkTheme}
                    enabled={enabled}
                  />
                ))}
              </Flex>
            </Box>
            {Action && <Action />}
          </Flex>
        </TabList>
        <TabPanels width={'100%'} overflow={'scroll'}>
          {panels.map((panel, index) => (
            <TabPanel overflow={'auto'} maxW={'100%'} p='0' key={index}>
              {panel}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
